import { TabsWithRoutes } from "@components/common/tabs/tabs-with-routes";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { MemberOverviewPage } from "@pages/profile/member-overview-page";
import { MemberTabs } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { NotFoundPage } from "@pages/not-found-page";
import { fetchCompanyMemberDetails } from "@store/members/members-slice-thunk";
import { useEffect } from "react";
import { fetchingMembersFlagsSelector } from "@store/members/members-selector";
import { companyMemberSelector } from "@store/members/members-selector";
import { resetCompanyMemberGroupsState } from "@store/company-member-groups/company-member-groups-slice";
import { OpenTabEvents } from "@utils/track-event/track-event-list";

/**
 * Content in the member page
 */
export function MemberPage(): JSX.Element {
  const { memberTabs } = useAppParams();
  const dispatch = useAppDispatch();
  const { companyId, memberId } = useAppParams();
  const companyMember = useAppSelector(companyMemberSelector(memberId));
  const { isFetchingCompanyMemberDetails } = useAppSelector(
    fetchingMembersFlagsSelector
  );

  // Fetches the member details
  useEffect(() => {
    if (companyId && memberId) {
      void dispatch(
        fetchCompanyMemberDetails({
          companyId,
          identity: memberId,
        })
      );
    }

    return () => {
      /* Clean the store for the company members group */
      dispatch(resetCompanyMemberGroupsState());
    };
  }, [companyId, dispatch, memberId]);

  // Show not found page if member tab is not valid
  if (!memberTabs || !Object.values(MemberTabs).includes(memberTabs)) {
    return <NotFoundPage />;
  }

  // Show not found page if there is not a valid member object after fetching is complete
  if (!companyMember && !isFetchingCompanyMemberDetails) {
    return <NotFoundPage />;
  }

  return (
    <TabsWithRoutes
      selectedTab={memberTabs}
      dataTestId="MEMBER_PAGE_CONTAINER"
      trackingEventPageName={OpenTabEvents.openMemberAccountTab}
      requiredAttribute={companyMember}
      tabs={[
        {
          label: "Overview",
          route: MemberTabs.overview,
          content: (user) => (
            <MemberOverviewPage
              member={user}
              isLoading={isFetchingCompanyMemberDetails}
              subjectType="member"
            />
          ),
          loadingContent: (
            <MemberOverviewPage
              member={null}
              isLoading={true}
              subjectType="member"
            />
          ),
        },
      ]}
    />
  );
}
